import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUSES = ["new", "contacted", "qualified", "proposal", "won", "lost"];

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_name: "", contact_name: "", email: "", phone: "", interest: "", notes: "", estimated_value: 0 });

  const load = async () => { const { data } = await api.get("/leads"); setLeads(data || []); };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => { await api.patch(`/leads/${id}`, { status }); load(); };

  const submit = async () => {
    try {
      await api.post("/leads", form);
      toast.success("Lead created");
      setOpen(false);
      setForm({ company_name: "", contact_name: "", email: "", phone: "", interest: "", notes: "", estimated_value: 0 });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="leads-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Pipeline</div>
          <h1 className="font-display text-4xl font-black tracking-tight mt-1">Leads</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-sm bg-[var(--uc-primary)] hover:bg-[var(--uc-primary-hover)] text-white" data-testid="new-lead-btn"><Plus size={16} className="mr-1" /> New lead</Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm max-w-lg">
            <DialogHeader><DialogTitle>New lead</DialogTitle></DialogHeader>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Company</Label><Input value={form.company_name} onChange={(e) => setForm({...form, company_name: e.target.value})} className="rounded-sm mt-1" data-testid="lead-company" /></div>
              <div><Label>Contact name</Label><Input value={form.contact_name} onChange={(e) => setForm({...form, contact_name: e.target.value})} className="rounded-sm mt-1" data-testid="lead-contact" /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="rounded-sm mt-1" data-testid="lead-email" /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="rounded-sm mt-1" /></div>
              <div className="sm:col-span-2"><Label>Interest</Label><Input value={form.interest} onChange={(e) => setForm({...form, interest: e.target.value})} className="rounded-sm mt-1" placeholder="e.g. Hosted PBX, SIP trunks" data-testid="lead-interest" /></div>
              <div><Label>Estimated value (CAD)</Label><Input type="number" value={form.estimated_value} onChange={(e) => setForm({...form, estimated_value: parseFloat(e.target.value || 0)})} className="rounded-sm mt-1" /></div>
              <div className="sm:col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="rounded-sm mt-1" /></div>
            </div>
            <DialogFooter><Button onClick={submit} className="rounded-sm bg-[var(--uc-primary)] hover:bg-[var(--uc-primary-hover)] text-white" data-testid="lead-save">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="rounded-sm border-border overflow-hidden">
        <table className="w-full text-sm dense">
          <thead className="bg-muted/40 text-left text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr><th>Company</th><th>Contact</th><th>Interest</th><th className="text-right">Est. value</th><th>Status</th><th>Created</th></tr>
          </thead>
          <tbody>
            {leads.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-muted-foreground">No leads yet.</td></tr>}
            {leads.map((l) => (
              <tr key={l.lead_id} className="border-t border-border" data-testid={`lead-${l.lead_id}`}>
                <td className="font-medium">{l.company_name}</td>
                <td>{l.contact_name}<div className="text-xs text-muted-foreground">{l.email}</div></td>
                <td className="text-muted-foreground">{l.interest}</td>
                <td className="text-right font-display font-bold">${(l.estimated_value || 0).toFixed(2)}</td>
                <td>
                  <Select value={l.status} onValueChange={(v) => setStatus(l.lead_id, v)}>
                    <SelectTrigger className="w-32 h-8 rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="text-muted-foreground text-xs">{l.created_at?.slice(0,10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
